import { Dropzone } from '@files-ui/react';
import { Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Accordion,
  AccordionDetails,
  type AccordionDetailsProps,
  type AccordionProps,
  AccordionSummary,
  type AccordionSummaryProps,
  Box,
  Button,
  TableCell,
  TableRow,
  Typography,
  accordionClasses,
  accordionSummaryClasses,
  styled,
  tableCellClasses,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React from 'react';

export const BorderedCard: React.FC<{
  label: string;
  icon: React.ReactNode;
  addLabel: string;
  onAdd: () => void;
  added?: boolean;
}> = ({ label, icon, addLabel, onAdd, added }) => (
  <Box
    sx={{
      px: 2,
      py: 2,
      position: 'relative',
      border: '1px solid',
      borderColor: grey[300],
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2">{label}</Typography>
    </Box>
    {added ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
        <CheckIcon fontSize="small" />
        <Typography variant="body2">Added</Typography>
      </Box>
    ) : (
      <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAdd}>
        {addLabel}
      </Button>
    )}
  </Box>
);

export const StyledTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#353b48',
  color: theme.palette.common.white,
}));

export const StyledTableRow = styled(TableRow)({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f5f6fa',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
});

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#353b48',
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const StyledBox = styled(Box)(() => ({
  marginBottom: 5,
  display: 'flex',
  alignItems: 'center',
  '.MuiChip-root:not(:last-of-type)': {
    marginRight: 5,
  },
}));

export const StyledDropzone = styled(Dropzone)(() => ({
  fontFamily: 'Roboto, sans-serif',
  fontSize: 18,
  borderColor: grey['400'],
  color: grey['400'],
}));

export const StyledAccordion = styled((props: AccordionProps) => <Accordion {...props} />)(() => ({
  [`&.${accordionClasses.expanded}`]: {
    '.MuiAccordionSummary-root': {
      borderBottom: `1px solid ${grey[200]}`,
    },
  },
}));

export const StyledAccordionSummary = styled((props: AccordionSummaryProps) => <AccordionSummary {...props} />)(() => ({
  [`.${accordionSummaryClasses.content}`]: {
    alignItems: 'center',
  },
}));

export const StyledAccordionDetails = styled((props: AccordionDetailsProps) => <AccordionDetails {...props} />)(() => ({
  backgroundColor: grey[50],
  [`.MuiInputBase-root`]: {
    backgroundColor: 'white',
  },
}));

export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => prop !== 'fontSize' && prop !== 'width',
})<{ fontSize?: string | number; width?: number | string }>(({ fontSize, width }) => ({
  fontSize: fontSize || '0.75rem', // Default to 0.75rem if no prop is passed
  width: width || 150, // Default width if no prop is passed
  '& .MuiInputBase-input': {
    fontSize: fontSize || '0.75rem',
  },
  '& .MuiAutocomplete-option': {
    fontSize: fontSize || '0.75rem',
  },
  '& .MuiFormLabel-root': {
    fontSize: fontSize || '0.75rem',
  },
}));

export const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'fontSize' && prop !== 'width',
})<{ fontSize?: string | number; width?: number | string }>(({ fontSize, width }) => ({
  '& .MuiInputBase-root': {
    maxWidth: width || 150,
    height: '35px',
    marginTop: '4px',
    fontSize: fontSize || 'inherit', // Inherit font size by default
    '& .MuiInputBase-input': {
      padding: '8px 10px',
      fontSize: fontSize || 'inherit',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
  '& .MuiFormLabel-root': {
    // Style the label
    fontSize: fontSize || 'inherit',
    top: 7,
  },
}));

export const StyledRegisterHead = styled(Box)(() => ({
  paddingLeft: 20,
  paddingRight: 20,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#E3F1FF',
  paddingX: '16px',
  height: 38,
}));
